import React, { useState, useEffect } from 'react'
import { Card, Typography, Row, Col, Table, DatePicker, Button, Space, message, Spin } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { httpPost } from '../../common/http.service'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface ShiftSchedule {
	group: number | string
	start: string
	end: string
}

interface ScheduleData {
	date: string
	shifts: ShiftSchedule[]
}

const DashboardTab: React.FC = () => {
	const [scheduleData, setScheduleData] = useState<ScheduleData[]>([])
	const [nightScheduleData, setNightScheduleData] = useState<ScheduleData[]>([])
	const [pmScheduleData, setPmScheduleData] = useState<ScheduleData[]>([])
	const [loading, setLoading] = useState(false)
	const [loadingNight, setLoadingNight] = useState(false)
	const [loadingPM, setLoadingPM] = useState(false)
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()])

	useEffect(() => {
		fetchShiftSchedules()
		fetchNightShiftSchedules()
		fetchPMShiftSchedules()
	}, [])

	const fetchShiftSchedules = async () => {
		setLoading(true)
		try {
			const response: any = await httpPost('/api/v1/admin/shift-schedules', {
				fromDate: dateRange[0].format('MM/DD/YYYY'),
				toDate: dateRange[1].format('MM/DD/YYYY'),
			})

			if (response) {
				setScheduleData(response)
			}
		} catch (error: any) {
			message.error(error?.message || 'Failed to load shift schedules')
		} finally {
			setLoading(false)
		}
	}

	const fetchNightShiftSchedules = async () => {
		setLoadingNight(true)
		try {
			const response: any = await httpPost(
				'/api/v1/admin/shift-schedules/schedule_shift_night',
				{
					fromDate: dateRange[0].format('MM/DD/YYYY'),
					toDate: dateRange[1].format('MM/DD/YYYY'),
				},
			)

			if (response) {
				setNightScheduleData(response)
			}
		} catch (error: any) {
			message.error(error?.message || 'Failed to load night shift schedules')
		} finally {
			setLoadingNight(false)
		}
	}

	const fetchPMShiftSchedules = async () => {
		setLoadingPM(true)
		try {
			const response: any = await httpPost(
				'/api/v1/admin/shift-schedules/schedule_pm_shift_night',
				{
					fromDate: dateRange[0].format('MM/DD/YYYY'),
					toDate: dateRange[1].format('MM/DD/YYYY'),
				},
			)

			if (response) {
				setPmScheduleData(response)
			}
		} catch (error: any) {
			message.error(error?.message || 'Failed to load PM shift schedules')
		} finally {
			setLoadingPM(false)
		}
	}

	const handleLoadAllSchedules = () => {
		fetchShiftSchedules()
		fetchNightShiftSchedules()
		fetchPMShiftSchedules()
	}

	const handleDateRangeChange = (dates: any) => {
		if (dates && dates[0] && dates[1]) {
			setDateRange([dates[0], dates[1]])
		}
	}

	const formatTime = (dateString: string) => {
		return dayjs(dateString).format('HH:mm')
	}

	const generateDynamicColumns = (data: ScheduleData[]) => {
		const groupSet = new Set<string | number>()
		data.forEach((item) => {
			item.shifts.forEach((shift) => {
				groupSet.add(shift.group)
			})
		})

		const sortedGroups = Array.from(groupSet).sort((a, b) => {
			if (typeof a === 'number' && typeof b === 'number') {
				return a - b
			}
			return String(a).localeCompare(String(b))
		})

		const dynamicColumns = sortedGroups.map((group) => ({
			title: typeof group === 'number' ? `Group ${group}` : group,
			key: `group_${group}`,
			render: (_: any, record: ScheduleData) => {
				const shift = record.shifts.find((s) => s.group === group)
				return shift ? `${formatTime(shift.start)} - ${formatTime(shift.end)}` : '-'
			},
		}))

		return [
			{
				title: 'Date',
				dataIndex: 'date',
				key: 'date',
				width: 150,
				render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
			},
			...dynamicColumns,
		]
	}

	const CARD_STYLE = {
		borderRadius: 12,
		boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
	}

	return (
		<Space direction="vertical" size={24} style={{ width: '100%' }}>
			<Card style={CARD_STYLE}>
				<Space size='large' wrap>
					<RangePicker
						value={dateRange}
						onChange={handleDateRangeChange}
						format='MM/DD/YYYY'
						style={{ minWidth: 250 }}
					/>
					<Button type='primary' onClick={handleLoadAllSchedules}>
						Load Schedules
					</Button>
				</Space>
			</Card>

			<Card title='Dynamic Shift Schedules' style={CARD_STYLE}>
				<Spin spinning={loading}>
					<Table
						columns={generateDynamicColumns(scheduleData)}
						dataSource={scheduleData}
						rowKey='date'
						pagination={{ pageSize: 10 }}
						scroll={{ x: 800 }}
						size="middle"
					/>
				</Spin>
			</Card>

			<Card title='Night Shift Schedules' style={CARD_STYLE}>
				<Spin spinning={loadingNight}>
					<Table
						columns={generateDynamicColumns(nightScheduleData)}
						dataSource={nightScheduleData}
						rowKey='date'
						pagination={{ pageSize: 10 }}
						scroll={{ x: 800 }}
						size="middle"
					/>
				</Spin>
			</Card>

			<Card title='PM Shift Schedules' style={CARD_STYLE}>
				<Spin spinning={loadingPM}>
					<Table
						columns={generateDynamicColumns(pmScheduleData)}
						dataSource={pmScheduleData}
						rowKey='date'
						pagination={{ pageSize: 10 }}
						scroll={{ x: 800 }}
						size="middle"
					/>
				</Spin>
			</Card>

			<Card title='Dashboard Summary' style={CARD_STYLE}>
				<Text type="secondary">Welcome to the Settings Dashboard. Use the sidebar to manage shift configurations, app versions, and fraud detection settings.</Text>
			</Card>
		</Space>
	)
}

export default DashboardTab
